import { useEffect, useRef, useState } from 'react';

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const useAnimatedDensity = (
  rowTarget: number,
  headerTarget: number,
  duration = 500,
) => {
  const [rowHeight, setRowHeight] = useState(rowTarget);
  const [headerHeight, setHeaderHeight] = useState(headerTarget);
  const rowRef = useRef(rowTarget);
  const headerRef = useRef(headerTarget);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    rowRef.current = rowHeight;
  }, [rowHeight]);

  useEffect(() => {
    headerRef.current = headerHeight;
  }, [headerHeight]);

  useEffect(() => {
    if (rowTarget === rowRef.current && headerTarget === headerRef.current) {
      return;
    }

    if (prefersReducedMotion()) {
      setRowHeight(rowTarget);
      setHeaderHeight(headerTarget);
      return;
    }

    const startRow = rowRef.current;
    const startHeader = headerRef.current;
    const startTime =
      typeof performance !== 'undefined' ? performance.now() : Date.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutCubic(progress);
      const nextRow = startRow + (rowTarget - startRow) * eased;
      const nextHeader = startHeader + (headerTarget - startHeader) * eased;
      setRowHeight(nextRow);
      setHeaderHeight(nextHeader);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [rowTarget, headerTarget, duration]);

  return { rowHeight, headerHeight };
};
